import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@sb/design-system';
import { useFeatureFlag } from '@sbrc/hooks';

import type { ChatHistorySession } from './ChatHistorySession';
import type { ChatMenuItemDef } from './ChatMenuItem';
import { ChatPopoverMenu } from './ChatPopoverMenu';

const ONE_MINUTE_MS = 60_000;
const ONE_HOUR_MS = 3_600_000;
const ONE_DAY_MS = 86_400_000;

export function formatRelativeTime(
  timestampMs: number,
  nowMs: number = Date.now(),
): string {
  const deltaMs = Math.max(0, nowMs - timestampMs);

  if (deltaMs < ONE_MINUTE_MS) return 'just now';

  if (deltaMs < ONE_HOUR_MS) {
    return `${Math.floor(deltaMs / ONE_MINUTE_MS)}m ago`;
  }

  if (deltaMs < ONE_DAY_MS) {
    return `${Math.floor(deltaMs / ONE_HOUR_MS)}h ago`;
  }

  return `${Math.floor(deltaMs / ONE_DAY_MS)}d ago`;
}

function isRoutineDeletedSession(session: ChatHistorySession): boolean {
  return session.routineID === null && session.routineName !== null;
}

function describeRoutine(session: ChatHistorySession): string {
  if (session.routineID === null) {
    return session.routineName
      ? `Routine deleted: ${session.routineName}`
      : 'No routine';
  }

  if (session.routineName === null) {
    return 'Routine';
  }

  return `Routine: ${session.routineName}`;
}

export interface ChatHistoryRowProps {
  session: ChatHistorySession;
  isActive: boolean;
  onLoad: () => void;
  onRename: (newTitle: string) => void;
  onRequestDelete: () => void;
}

const ICON_BUTTON_CLASS = cx(
  'tw-flex',
  'tw-items-center',
  'tw-justify-center',
  'tw-w-28',
  'tw-h-28',
  'tw-border-none',
  'tw-cursor-pointer',
  'tw-p-0',
  'tw-shrink-0',
);

// 8px radius isn't in the radius scale (6 → 10); muted icon color
// and the transition curve don't have tokens.
const ICON_BUTTON_INLINE_STYLE: React.CSSProperties = {
  borderRadius: 8,
  background: 'none',
  color: 'var(--label-secondary)',
  transition: 'background 0.12s ease',
};

/**
 * Single chat-session row. The whole row is clickable to load the
 * session; a kebab on the right opens a `ChatPopoverMenu` (Load /
 * Rename / Delete). Rename happens inline.
 *
 * Active session is highlighted with a 5%-orange tint (a very light
 * orange-grey, not a fully saturated brand fill); hover applies a
 * subtle 4%-black tint.
 */
export function ChatHistoryRow({
  session,
  isActive,
  onLoad,
  onRename,
  onRequestDelete,
}: ChatHistoryRowProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(session.title);
  const [isHovered, setIsHovered] = useState(false);

  const renameInputRef = useRef<HTMLInputElement>(null);
  // Guard so onBlur doesn't re-enter commit after Enter/Escape.
  const isCommitFinishedRef = useRef(false);

  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [isRenaming]);

  const commitRename = () => {
    if (isCommitFinishedRef.current) {
      isCommitFinishedRef.current = false;

      return;
    }

    const trimmed = renameValue.trim();

    if (trimmed && trimmed !== session.title) {
      onRename(trimmed);
    } else {
      setRenameValue(session.title);
    }

    isCommitFinishedRef.current = true;
    setIsRenaming(false);
  };

  const cancelRename = () => {
    isCommitFinishedRef.current = true;
    setRenameValue(session.title);
    setIsRenaming(false);
  };

  const startRenaming = () => {
    setRenameValue(session.title);
    isCommitFinishedRef.current = false;
    setIsRenaming(true);
  };

  const isRoutineDeleted = isRoutineDeletedSession(session);
  const routineLabel = describeRoutine(session);

  const menuItems: ChatMenuItemDef[] = [
    {
      label: 'Load',
      iconKind: 'arrowUturnLeft',
      onClick: onLoad,
      disabled: isRoutineDeleted,
    },
    {
      label: 'Rename',
      iconKind: 'pencil',
      onClick: startRenaming,
    },
    {
      label: 'Delete',
      iconKind: 'trash',
      onClick: onRequestDelete,
      destructive: true,
    },
  ];

  // Light-orange-grey highlight for active; hover deepens slightly.
  const isDark = useFeatureFlag('darkMode');

  const rowBackground = (() => {
    // Active = a brand-orange accent tint. There's no orange-tint token,
    // and one opacity can't serve both themes: 8% orange reads on white
    // but nearly disappears on the dark panel (where the neutral hover
    // fill would then look stronger than the selection). So the opacity
    // is picked per theme. Hover uses the themeable `--fill-primary`.
    if (isActive) {
      return isDark ? 'rgba(250,103,30,0.20)' : 'rgba(224,79,27,0.08)';
    }

    // The menu is portaled out of the row, so opening it fires
    // onMouseLeave — hold the tint while it's open so the floating
    // menu still reads as belonging to this row.
    if (isHovered || isMenuOpen) return 'var(--fill-primary)';

    return 'transparent';
  })();

  const handleRowClick = () => {
    if (isRenaming || isMenuOpen) return;
    if (isRoutineDeleted) return;
    onLoad();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Load chat: ${session.title}`}
      aria-disabled={isRoutineDeleted}
      onClick={handleRowClick}
      onKeyDown={(e) => {
        // Only the row's own keystrokes activate it. Keys typed into a
        // descendant — notably the inline rename input — bubble up here,
        // and swallowing Space with preventDefault would stop the input
        // from accepting spaces in a title.
        if (e.target !== e.currentTarget) return;

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
      data-testid="chat-history-session-row"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cx(
        'tw-relative',
        'tw-flex',
        'tw-items-start',
        'tw-gap-10',
        'tw-py-10',
        'tw-px-8',
        'tw-rounded-10',
        'tw-outline-none',
      )}
      style={{
        // Conditional background (active orange tint / hover grey /
        // transparent) and cursor stay inline.
        background: rowBackground,
        cursor: isRoutineDeleted ? 'default' : 'pointer',
        transition: 'background 0.12s ease',
      }}
    >
      {/* 8×8 active dot — wrap in a line box matching the title's
          font metrics so flex aligns the box (not the dot itself)
          with the title's text line. Inside that box, the dot is
          vertically centered, so it sits at the title's text-center
          even when the row is taller than the title alone. The
          marginTop nudge accounts for the typography ascent: the
          line-box's geometric center sits slightly above the
          glyph's optical center. */}
      <div
        aria-hidden
        className={cx(
          'tw-flex',
          'tw-items-center',
          'tw-justify-center',
          'tw-w-8',
          'tw-text-13',
          'tw-shrink-0',
        )}
        // 1.2em line-box + 3px top nudge are layout-specific values
        // without spacing tokens.
        style={{ height: '1.2em', marginTop: 3 }}
      >
        <div
          className="tw-w-8 tw-h-8 tw-rounded-full"
          style={{
            background: isActive ? 'var(--orange)' : 'transparent',
          }}
        />
      </div>

      <div className="tw-flex tw-flex-1 tw-min-w-0 tw-flex-col">
        {isRenaming ? (
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            data-testid="chat-history-rename-field"
            onKeyDown={(e) => {
              e.stopPropagation();

              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelRename();
              }
            }}
            aria-label="Rename chat session"
            className={cx(
              'tw-w-full',
              'tw-rounded-6',
              'tw-py-4',
              'tw-px-6',
              'tw-text-13',
              'tw-font-medium',
              'tw-outline-none',
            )}
            style={{
              // Inherited font family doesn't map to a token; border /
              // fill / text use themeable tokens. The fill uses
              // `--fill-primary` so the input stays distinct from the
              // row/panel surface in both themes.
              border: '1px solid var(--divider-primary)',
              background: 'var(--fill-primary)',
              color: 'var(--label-primary)',
              fontFamily: 'inherit',
            }}
          />
        ) : (
          <div
            className={cx(
              'tw-text-13',
              'tw-font-medium',
              'tw-overflow-hidden',
              'tw-text-ellipsis',
              'tw-whitespace-nowrap',
            )}
            style={{
              color: isRoutineDeleted
                ? 'var(--label-quaternary)'
                : 'var(--label-primary)',
            }}
          >
            {session.title}
          </div>
        )}
        <div
          className={cx(
            'tw-mt-2',
            'tw-overflow-hidden',
            'tw-text-ellipsis',
            'tw-whitespace-nowrap',
          )}
          style={{
            // 11px font size isn't in the typography scale (starts at
            // 13); subtitle color and italic-on-deleted stay inline.
            fontSize: 11,
            color: isRoutineDeleted
              ? 'var(--label-quaternary)'
              : 'var(--label-tertiary)',
            fontStyle: isRoutineDeleted ? 'italic' : 'normal',
          }}
        >
          {routineLabel} · {formatRelativeTime(session.lastUpdatedAt)}
        </div>
      </div>

      <ChatPopoverMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        items={menuItems}
        ariaLabel="Chat session actions"
        className="tw-flex tw-shrink-0"
        data-testid="chat-history-session-actions-menu"
      >
        <button
          type="button"
          aria-label="Chat session actions"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          data-testid="chat-history-session-actions-button"
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen((open) => !open);
          }}
          className={ICON_BUTTON_CLASS}
          style={ICON_BUTTON_INLINE_STYLE}
          onMouseEnter={(e) => {
            // eslint-disable-next-line no-param-reassign -- imperative hover background paired below
            e.currentTarget.style.background = 'var(--fill-primary)';
          }}
          onMouseLeave={(e) => {
            // eslint-disable-next-line no-param-reassign -- imperative hover background paired below
            e.currentTarget.style.background = 'none';
          }}
        >
          <Icon
            kind="ellipsisCircle"
            // 18×18 icon isn't in the icon-size scale.
            style={{ width: 18, height: 18 }}
          />
        </button>
      </ChatPopoverMenu>
    </div>
  );
}
